import {isFeatureEnabled} from '@github-ui/feature-flags'
import {UnderlinePanels} from '@primer/react/experimental'

import {TEST_IDS} from '../../constants/test-ids'
import type {IssueTimelineIssueFragment$data} from './__generated__/IssueTimelineIssueFragment.graphql'
import type {EventProps} from './IssueTimelineItem'
import styles from './TabbedTimeline.module.css'
import {TimelinePanel} from './TimelinePanel'
import {useTimelineItems} from './use-timeline-items'

type TabbedTimelineProps = {
  data: IssueTimelineIssueFragment$data
  highlightedEvent: string | undefined
  page?: number
  isTransferInProgress?: boolean
  showCopilotMenuItem?: boolean
} & EventProps

/**
 * Tabbed timeline view that will split events into "Conversation" and "Activity" tabs.
 *
 * Currently uses the classic (unfiltered) timeline data for the Conversation tab.
 * The Activity tab is a placeholder — filtered data will be wired in once the
 * query-level changes are in place.
 *
 * Gated behind the `issues_react_timeline_tabs` feature flag.
 */
export function TabbedTimeline({
  data,
  highlightedEvent,
  page,
  isTransferInProgress,
  showCopilotMenuItem,
  viewer,
  onCommentChange,
  onCommentReply,
  onCommentEditCancel,
  optionConfig,
}: TabbedTimelineProps) {
  const timelinePaginationForBotsEnabled = isFeatureEnabled('issues_react_bots_timeline_pagination')
  const effectivePage = timelinePaginationForBotsEnabled ? page : undefined

  const {timelineItems, totalItemCount, highlightedReady} = useTimelineItems({
    timelineData: data,
    highlightedEvent,
    page: effectivePage,
  })

  return (
    <UnderlinePanels aria-label="Timeline" className={styles.tabbedNav} data-testid={TEST_IDS.tabbedTimelineNav}>
      <UnderlinePanels.Tab aria-selected counter={totalItemCount}>
        Conversation
      </UnderlinePanels.Tab>
      <UnderlinePanels.Tab>Activity</UnderlinePanels.Tab>

      <UnderlinePanels.Panel data-testid={TEST_IDS.tabbedTimelineConversationPanel}>
        <TimelinePanel
          timelineItems={timelineItems}
          totalItemCount={totalItemCount}
          highlightedEvent={highlightedEvent}
          highlightedReady={highlightedReady}
          issueId={data.id}
          repositoryId={data.repository.id}
          repositoryNameWithOwner={data.repository.nameWithOwner}
          issueUrl={data.url}
          page={effectivePage}
          isTransferInProgress={isTransferInProgress}
          showCopilotMenuItem={showCopilotMenuItem}
          viewer={viewer}
          onCommentChange={onCommentChange}
          onCommentReply={onCommentReply}
          onCommentEditCancel={onCommentEditCancel}
          optionConfig={optionConfig}
        />
      </UnderlinePanels.Panel>

      <UnderlinePanels.Panel data-testid={TEST_IDS.tabbedTimelineActivityPanel}>
        {/* Activity panel — placeholder until filtered data is available */}
      </UnderlinePanels.Panel>
    </UnderlinePanels>
  )
}
